import * as UUID from 'uuid'
import * as React from 'react'
import { FunctionComponent, PropsWithChildren, ReactElement, useContext, useEffect, useState } from 'react'
import ErrorBoundary from '../components/Diagnostics/ErrorBoundary'
import Router from './Router'
import { FileProvider } from '../context/FileContext'
import { UserProvider } from '../context/UserContext'
import { MessageProvider } from '../context/MessageContext'
import MainMenu from '../components/MainMenu'
import Errors from '../components/Diagnostics/Errors'
import Commands from './Commands'
import { MemoryRouter } from 'react-router-dom'
import axios from '../helpers/axiosHelper'
import AxiosContext from '../context/AxiosContext'
import { Route } from 'react-router'
import { Stack, StackItem } from '@fluentui/react'
import { GlobalProvider } from '../context/GlobalContext'
import { FluentProvider, makeStyles, shorthands, tokens, webLightTheme } from '@fluentui/react-components'
import { AzureWebSocketProvider } from '../context/AzureWebSocketContext'
import { ToastContainer } from 'react-toastify'
import Authentication from '../components/Authentication/Authentication'
import IdentityContext, { IdentityProvider } from '../context/IdentityContext'
import Version from '../components/Diagnostics/Version'
import Support from '../components/Diagnostics/Support'
import FileLink from '../components/Quicklink/FileLink'
import useHost from '../hooks/useHost'
import Loading from '../components/Loading'
import { GenerationQueueProvider } from '../context/QueueContext'
import { MollieProvider } from '../context/MollieContext'
import { useTranslation } from 'react-i18next'
import { AppInsightsContext, ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { I18NProvider } from '../context/I18NContext'
import { isOfficeApplication, isOfficeInitialized } from '../helpers/outlookHelper'
import MailboxAvatar from '../components/Diagnostics/MailboxAvatar'
import useLocalStorage from '../hooks/useLocalStorage'
import FirstVisit from './Intro/FirstVisit'
import { OfficeEventHandlerProvider } from '../context/OfficeEventHandlerContext'

export const appInsightsReact = new ReactPlugin()
export const appInsights = new ApplicationInsights({
    config: {
        connectionString:
            'InstrumentationKey=417924ef-4814-4e9a-880b-69516abc7d57;IngestionEndpoint=https://westeurope-5.in.applicationinsights.azure.com/;LiveEndpoint=https://westeurope.livediagnostics.monitor.azure.com/;ApplicationId=50e2ffce-054d-4094-927f-027fc4b27e89',
        extensions: [appInsightsReact],
        enableAutoRouteTracking: true,
        autoTrackPageVisitTime: true,
    },
})
if (!isOfficeInitialized() || isOfficeApplication()) {
    appInsights.loadAppInsights()
}

export const useApplicationStyles = makeStyles({
    wrapper: {
        backgroundColor: '#eff6fc',
        backgroundImage: "url('../images/hammock-bg.png')",
        backgroundSize: '1200px 600px',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        minHeight: '100%',
    },
    container: {
        minWidth: 0,
        maxWidth: '1280px',
        backgroundColor: tokens.colorNeutralBackground2,
        boxShadow: tokens.shadow16,

        '> :not(:first-child)': {
            marginTop: tokens.spacingVerticalM,
        },
    },
    accordionContent: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    content: {
        display: 'flex',
    },
    header: {
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        height: '40px',
    },
    subMenu: {
        position: 'sticky',
        top: '44px',
        zIndex: 7,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    actionItems: {
        position: 'sticky',
        top: '88px',
        zIndex: 8,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    footer: {
        position: 'sticky',
        bottom: 0,
        zIndex: 1000,
        height: '40px',
        backgroundColor: tokens.colorBrandBackground2,
    },
    footerSection: {
        display: 'flex',
        gap: tokens.spacingVerticalS,
    },
    thinTextButton: {
        fontWeight: tokens.fontWeightRegular,
    },
    selfCenter: {
        alignSelf: 'center',
    },
    verticalAlign: {
        verticalAlign: 'middle',
    },
    alignSelf: {
        alignSelf: 'center',
    },
})

const Template: FunctionComponent<PropsWithChildren> = props => {
    const { authenticated } = useContext(IdentityContext)
    const styles = useApplicationStyles()

    return (
        <Stack id={'wrapper'} horizontal horizontalAlign="center" className={styles.wrapper}>
            <Stack id={'contentcontainer'} grow className={styles.container}>
                {authenticated && (
                    <Stack className={styles.header}>
                        <MainMenu />
                    </Stack>
                )}
                <Errors />
                <Stack grow className={styles.content}>
                    {authenticated && (
                        <>
                            {props.children}
                            <Commands />
                            <ToastContainer position="bottom-right" stacked />
                        </>
                    )}

                    {!authenticated && <Authentication />}
                </Stack>
                <Stack horizontal wrap horizontalAlign="space-between" className={styles.footer}>
                    <StackItem className={styles.footerSection}>
                        <FileLink />
                    </StackItem>
                    <StackItem className={styles.footerSection}>
                        {authenticated && <Support />}
                        {authenticated && <MailboxAvatar />}
                        <Version />
                    </StackItem>
                </Stack>
            </Stack>
        </Stack>
    )
}

const InsightsContainer: FunctionComponent<PropsWithChildren> = props => {
    const host = useHost()
    const { authenticated, user } = useContext(IdentityContext)
    const { i18n } = useTranslation()

    useEffect(() => {
        // Route to the correct base path so that each user gets the correct version served.
        if (host?.ClientPath && window.location.pathname !== host.ClientPath) {
            // Redirect if we're not in the correct environment.
            // window.location.href = host.clientPath + window.location.search + window.location.hash
            const newLocation = host.ClientPath + window.location.search + (window.location.hash || '#') + window.location.pathname
            window.location.replace(newLocation)
        }
    }, [host])

    useEffect(() => {
        if ((!isOfficeInitialized() || isOfficeApplication()) && authenticated && user) {
            appInsights.setAuthenticatedUserContext(user.id, host.RowKey, true)
            appInsights.trackEvent({
                name: 'i18n',
                properties: {
                    language: i18n.language,
                },
            })
        }
    }, [authenticated, user])

    return <AppInsightsContext.Provider value={appInsightsReact}>{props.children}</AppInsightsContext.Provider>
}

const Container: FunctionComponent<PropsWithChildren> = () => {
    const { t: translate } = useTranslation()
    const { authenticated, user } = useContext(IdentityContext)
    const host = useHost()

    useEffect(() => {
        // Route to the correct base path so that each user gets the correct version served.
        if (host?.ClientPath && window.location.pathname !== host.ClientPath) {
            // Redirect if we're not in the correct environment.
            // window.location.href = host.clientPath + window.location.search + window.location.hash
            const newLocation = host.ClientPath + window.location.search + (window.location.hash || '#') + window.location.pathname
            window.location.replace(newLocation)
        }
    }, [host])

    const { values: firstVisit, setValues: setFirstVisit } = useLocalStorage<boolean, boolean>({
        key: 'firstVisit',
        defaultValues: true,
        fromJson: values => values,
        toJson: values => values,
    })
    const { values: identity } = useLocalStorage<{ mailbox: string }, { mailbox: string }>({
        key: 'identity',
        readOnly: true,
        defaultValues: { mailbox: undefined },
        fromJson: values => values,
        toJson: values => values,
    })

    useEffect(() => {
        if (firstVisit && identity.mailbox && !(process.env.NODE_ENV === 'development')) {
            setFirstVisit(false)
        }
    }, [firstVisit, identity])

    if (host?.ClientPath && window.location.pathname !== host.ClientPath) {
        return <Loading label={translate('authentication.host.redirect')} />
    }

    if (firstVisit) {
        return <FirstVisit setVisited={() => setFirstVisit(false)} />
    }

    if (!authenticated) {
        return (
            <Template>
                <Authentication />
            </Template>
        )
    }

    return (
        <MemoryRouter initialEntries={[window.location.hash.substring(1)]}>
            <GlobalProvider>
                <GenerationQueueProvider>
                    <FileProvider>
                        <MessageProvider>
                            <UserProvider userId={user.id} userEmail={user.email}>
                                <MollieProvider>
                                    <AzureWebSocketProvider>
                                        <OfficeEventHandlerProvider>
                                            <Template>
                                                <Route path="/" component={Router} />
                                            </Template>
                                        </OfficeEventHandlerProvider>
                                    </AzureWebSocketProvider>
                                </MollieProvider>
                            </UserProvider>
                        </MessageProvider>
                    </FileProvider>
                </GenerationQueueProvider>
            </GlobalProvider>
        </MemoryRouter>
    )
}

export default function App(): ReactElement {
    const [currentVersion] = useState(process.env.COMMITHASH)
    const [currentVersionTimeout, setCurrentVersionTimeout] = useState<NodeJS.Timeout>(undefined)

    const fetchNewVersion = () => {
        const commitHashUrl = window.location.origin + (window.location.pathname + '/COMMITHASH').replaceAll('//COMMITHASH', '/COMMITHASH')

        fetch(commitHashUrl, { cache: 'no-cache', headers: { 'Cache-Control': 'no-cache' } })
            .then(response => {
                if (response.ok) {
                    return response.text()
                } else {
                    console.log('Could not load /COMMITHASH: status: ' + response.status)
                    return currentVersion
                }
            })
            .then(response => (UUID.validate(response) ? response : currentVersion))
            .then((newVersion: string) => {
                console.log(`Update check currentVersion=${currentVersion} , newVersion=${newVersion}`)
                if (currentVersion !== newVersion) {
                    window.location.reload()
                } else {
                    setCurrentVersionTimeout(setTimeout(() => fetchNewVersion(), 3600000))
                }
            })
            .catch(error => console.log(error))
    }

    useEffect(() => {
        fetchNewVersion()

        return () => currentVersionTimeout && clearTimeout(currentVersionTimeout)
    }, [])

    return (
        <FluentProvider theme={webLightTheme}>
            <I18NProvider>
                <ErrorBoundary>
                    <AxiosContext.Provider value={axios}>
                        <IdentityProvider>
                            <InsightsContainer>
                                <Container />
                            </InsightsContainer>
                        </IdentityProvider>
                    </AxiosContext.Provider>
                </ErrorBoundary>
            </I18NProvider>
        </FluentProvider>
    )
}
